import { Point2D } from "@dextall/common-utilities";
import { Selection, SelectionType } from "@dextall/panel-hooks-editor-2d";
import { HooksModelEditor } from "../../editors/hooksModelEditor";
import { PanelsModelEditor } from "../../editors/panelsModelEditor";
import { IViewerAggregateSelectionChangedEventPayload } from "../../viewer-utils/viewerEventPayloads";
import { CornerHook } from "../cornerHook";
import { Hook } from "../hook";
import { IModelHook } from "../modelHook";
import { Panel } from "../panel";
import {
    convertPanelEditorPositionToModelCoordinates,
    createEditorCladdingCells,
    createPanelEditorHook,
} from "./editorTypesConverter";
import { PanelEditorContent } from "./panelEditorContent";
import { RefreshEventPayload } from "../../eventBus/hooksDesignerEventPayloads";
import eventBus, { IApplicationEvent } from "../../eventBus/eventDispatcher";

export class PanelEditorScope {
    private readonly _panelEditorContent: PanelEditorContent;
    private selectedPanelTypeId: string | null = null;
    private selectedPanelId: string | null = null;

    constructor(
        private readonly panelsEditor: PanelsModelEditor,
        private readonly hooksEditor: HooksModelEditor,
        private readonly viewer: Autodesk.Viewing.GuiViewer3D,
    ) {
        this.setComponentSelection = this.setComponentSelection.bind(this);
        this.updateHook = this.updateHook.bind(this);
        this.addHook = this.addHook.bind(this);
        this.removeHook = this.removeHook.bind(this);
        this.onSelectionChanged = this.onSelectionChanged.bind(this);
        this.onHookUpdated = this.onHookUpdated.bind(this);
        this.onRefresh = this.onRefresh.bind(this);
        this._panelEditorContent = {
            panelDefinition: { claddingCells: [], windows: [], louvres: [] },
            hooks: [],
            selection: null,
            setComponentSelection: this.setComponentSelection,
            updateHook: this.updateHook,
            addHook: this.addHook,
            removeHook: this.removeHook,
        };

        this.viewer.addEventListener(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, this.onSelectionChanged);
        eventBus.addEventListener("Dextall.Hooks.Designer.ModelHookUpdated", this.onHookUpdated);
        eventBus.addEventListener("Dextall.Hooks.Designer.Refresh", this.onRefresh);
    }

    get panelEditorContent(): PanelEditorContent | null {
        return this.selectedPanelTypeId !== null ? this._panelEditorContent : null;
    }

    dispose() {
        this.viewer.removeEventListener(Autodesk.Viewing.AGGREGATE_SELECTION_CHANGED_EVENT, this.onSelectionChanged);
        eventBus.removeEventListener("Dextall.Hooks.Designer.ModelHookUpdated", this.onHookUpdated);
        eventBus.removeEventListener("Dextall.Hooks.Designer.Refresh", this.onRefresh);
        // @ts-expect-error help GC, the component is not intended to be used after dispose
        delete this._panelEditorContent.setComponentSelection;
        // @ts-expect-error help GC, the component is not intended to be used after dispose
        delete this._panelEditorContent.updateHook;
        // @ts-expect-error help GC, the component is not intended to be used after dispose
        delete this._panelEditorContent.addHook;
        // @ts-expect-error help GC, the component is not intended to be used after dispose
        delete this._panelEditorContent.removeHook;
        // @ts-expect-error help GC, the component is not intended to be used after dispose
        delete this._panelEditorContent;
    }

    private setComponentSelection(selection: Selection | null, suppressViewerSelection = false) {
        this._panelEditorContent.selection = selection;

        this.raiseComponentSelectionChanged();

        if (suppressViewerSelection) {
            return;
        }

        const panel = this.getSelectedPanel();

        if (selection?.type === SelectionType.Hook) {
            const panelHooks = this.hooksEditor.findPanelHooks(panel);
            const hook = panelHooks[selection.index];

            this.viewer.select(hook.dbId, this.panelsEditor.model);
        } else {
            this.viewer.select(panel.panelDbId, this.panelsEditor.model);
        }
    }

    private updateHook(index: number, newPosition: Point2D) {
        const panel = this.getSelectedPanel();
        const panelHooks = this.hooksEditor.findPanelHooks(panel);
        const hook = panelHooks[index];

        const payload: IModelHook = {
            dbId: hook.dbId,
            panelId: hook.panelId,
            cornerHookType: hook.cornerHookType,
            hookType: hook.hookType,
            profileSide: hook.profileSide,
            withPin: hook.withPin,
            ...convertPanelEditorPositionToModelCoordinates(newPosition),
        };

        eventBus.dispatchEvent({ type: "Dextall.Hooks.UI.PanelHook.ParameterChanged", payload });
    }

    private addHook(newHookPosition: Point2D) {
        const selectedPanel = this.getSelectedPanel();

        const targetPosition = convertPanelEditorPositionToModelCoordinates(newHookPosition);

        this.hooksEditor.createNewPanelHook(selectedPanel.id, new THREE.Vector2(targetPosition.x, targetPosition.y));

        this.setHooks(selectedPanel);
        this.setComponentSelection({
            type: SelectionType.Hook,
            index: this._panelEditorContent.hooks.length - 1,
        });
    }

    private removeHook(index: number) {
        const panel = this.getSelectedPanel();
        const panelHooks = this.hooksEditor.findPanelHooks(panel);
        const hook = panelHooks[index];

        this.hooksEditor.removeHook(hook);
        
        this.setHooks(panel);

        this.setComponentSelection(null);
    }

    private onSelectionChanged(event: IViewerAggregateSelectionChangedEventPayload) {
        const selectedDbId = event.selections
            .filter(x => x.model.id === this.panelsEditor.modelId)
            .flatMap(x => x.dbIdArray)
            .find(x => x);

        if (selectedDbId === undefined) {
            this.selectedPanelTypeId = null;
            return;
        }

        const selectedPanel = this.panelsEditor.findPanel(selectedDbId);

        if (selectedPanel) {
            this.selectedPanelId = selectedPanel.id;
            if (this._panelEditorContent.selection?.type === SelectionType.Hook) {
                this.setComponentSelection(null, true);
            }

            if (this.selectedPanelTypeId === selectedPanel.panelTypeId) {
                return;
            }
            this.setNewPanelContent(selectedPanel);

            return;
        }

        const selectedHook = this.hooksEditor.findHook(selectedDbId);

        if (selectedHook instanceof Hook) {
            const parentPanel = this.getHookParentPanel(selectedHook);

            if (parentPanel.panelTypeId !== this.selectedPanelTypeId) {
                this.setNewPanelContent(parentPanel);
            }
            this.setHookComponentSelection(parentPanel, selectedHook);
            this.selectedPanelId = parentPanel.id;
        } else {
            this.selectedPanelTypeId = null;
        }
    }

    private onHookUpdated(event: IApplicationEvent<Hook | CornerHook>) {
        const hook = event.payload;
        if (!(hook instanceof Hook)) {
            return;
        }

        const parentPanel = this.getHookParentPanel(hook);
        if (parentPanel.panelTypeId === this.selectedPanelTypeId) {
            this.setHooks(parentPanel);
        }
    }

    private onRefresh(event: IApplicationEvent<RefreshEventPayload>) {
        // TODO: review dispatching Dextall.Hooks.Designer.Refresh event
        // it inconsistently puts either panelId or panelTypeId in the payload
        const { panelId: panelOrTypeId } = event.payload;

        const isRefreshingCurrentPanelType = panelOrTypeId === this.selectedPanelTypeId
            || this.panelsEditor.findPanelById(panelOrTypeId)?.panelTypeId === this.selectedPanelTypeId;

        if (!isRefreshingCurrentPanelType) {
            return;
        }

        const panel = this.getSelectedPanel();
        this.setHooks(panel);

        if (
            this._panelEditorContent.selection?.type === SelectionType.Hook &&
            this._panelEditorContent.selection.index >= this._panelEditorContent.hooks.length
        ) {
            this.setComponentSelection(null);
        }
    }

    private setNewPanelContent(panel: Panel) {
        this._panelEditorContent.panelDefinition = {
            claddingCells: createEditorCladdingCells(panel),
            windows: panel.windows.map(x => x.definition),
            louvres: panel.louvres.map(x => x.definition),
        };
        this.setHooks(panel);
        this.setComponentSelection(null, true);

        this.selectedPanelTypeId = panel.panelTypeId;
    }

    private setHooks(panel: Panel) {
        this._panelEditorContent.hooks = this.hooksEditor.findPanelHooks(panel).map(createPanelEditorHook);
    }

    private setHookComponentSelection(panel: Panel, hook: Hook) {
        const panelHooks = this.hooksEditor.findPanelHooks(panel);
        const index = panelHooks.indexOf(hook);

        if (
            this._panelEditorContent.selection?.type !== SelectionType.Hook ||
            this._panelEditorContent.selection.index !== index
        ) {
            this._panelEditorContent.selection = { type: SelectionType.Hook, index };
        }
    }

    /** Gets currently selected panel or throws an exception if not found */
    private getSelectedPanel(): Panel {
        if (this.selectedPanelId === null) {
            throw new Error("No panel is selected.");
        }
        const panel = this.panelsEditor.findPanelById(this.selectedPanelId);
        if (!panel) {
            throw new Error(`Panel with id ${this.selectedPanelId} not found.`);
        }
        return panel;
    }

    /**
     * Gets hook parent panel or throws if not found (which should never happen).
     */
    private getHookParentPanel(hook: Hook): Panel {
        const parentPanel = this.panelsEditor.findPanelById(hook.panelId);
        if (!parentPanel) {
            throw new Error(`Parent panel with id ${hook.panelId} not found for the selected hook.`);
        }

        return parentPanel;
    }

    private raiseComponentSelectionChanged() {
        eventBus.dispatchEvent({ type: "Dextall.Hooks.Designer.Panel.ComponentSelectionChanged", payload: null });
    }
}
